import "./JsonEditor.scss";

import { useEffect, useRef } from "react";
import {
  type Content,
  createJSONEditor,
  type MenuItem,
} from "vanilla-jsoneditor";

export interface JsonEditorProps {
  initialValue: any;
  shoMenuBar?: boolean;
  hasChanges?: boolean;
  isCustomUndo?: boolean;
  startValue?: any;
  className?: string;
  withValidation?: boolean;
  onChange: (value: any) => void;
  setCustomUndo?: (value: boolean) => void;
  validator?: (value: any) => any;
  onError?: (error: any) => void;
  autofocus?: boolean;
}

const getUndoRedo = (menu: MenuItem[]) => {
  return menu.filter(
    item =>
      item.type === "button" &&
      (item.className === "jse-redo" || item.className === "jse-undo"),
  );
};

const JsonEditor: React.FC<JsonEditorProps> = props => {
  const {
    initialValue,
    shoMenuBar,
    isCustomUndo,
    startValue,
    hasChanges,
    withValidation = true,
    autofocus,
    validator,
    setCustomUndo,
    onChange,
    onError,
  } = props;

  const editorRefContainer = useRef<HTMLDivElement>(null);
  const refEditor = useRef<any>(null);

  useEffect(() => {
    const content =
      typeof initialValue === "object"
        ? {
            json: initialValue,
          }
        : {
            text: "",
          };
    refEditor.current = createJSONEditor({
      target: editorRefContainer.current!,
      props: {
        content,
        mode: "code",
        mainMenuBar: !!shoMenuBar,
        navigationBar: false,
        statusBar: false,
        validator: validator,
        onChange: (value: Content) => {
          if (!withValidation) {
            onChange(value);
            return;
          }
          setCustomUndo && setCustomUndo(false);
          try {
            if ("text" in value) {
              if (value.text) {
                onChange(JSON.parse(value.text));
              } else {
                onChange("");
              }
            }
          } catch (error) {
            console.log("error", error);
            onError && onError(!!error);
            return;
          }
          onError && onError(false);
        },
        onRenderMenu: getUndoRedo,
      },
    });

    if (autofocus && refEditor.current) {
      setTimeout(() => {
        refEditor.current?.focus();
      });
    }

    return () => {
      if (refEditor.current) {
        refEditor.current.destroy();
        refEditor.current = null;
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const content =
      typeof initialValue === "object"
        ? {
            json: initialValue,
          }
        : {
            text: "",
          };
    refEditor.current.set(content);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initialValue)]);

  useEffect(() => {
    if (refEditor.current) {
      refEditor.current.updateProps({
        onRenderMenu: (menu: MenuItem[]) => {
          const [undo, redo] = getUndoRedo(menu);
          return isCustomUndo
            ? [
                {
                  ...undo,
                  disabled: !hasChanges,
                  onClick: () => {
                    refEditor.current.set({
                      json: JSON.parse(startValue),
                    });
                    onChange(JSON.parse(startValue));
                    setCustomUndo && setCustomUndo(false);
                  },
                },
                redo,
              ]
            : [undo, redo];
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startValue, hasChanges, isCustomUndo]);

  return (
    <div
      className={`json-editor ${shoMenuBar ? "with-menu-bar" : ""}`}
      ref={editorRefContainer}
    />
  );
};

export { JsonEditor };
